import type { FunctionComponent, ReactNode } from 'react';
import React, { useRef, useEffect, useState, useMemo } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { motion, AnimatePresence } from 'framer-motion';
import isEmpty from 'lodash/isEmpty';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';

import type { Direction } from '../../steps/types/step';
import { Loader } from '../loader/Loader';
import { themeSVGBase64 } from '../../utils/image';
import { getStepTrustHeaderText } from '../../utils/step_header_text';
import { TrustText } from '../trust_text/TrustText';
import { useStepManager } from '../../steps';
import { TrustReviewCard } from '../customer_reivew_card/TrustReviewCard';
import { TrustAndDownloadCard } from '../customer_reivew_card/TrustAndDownloadCard';
import type { StepIndicatorDataType } from '../../steps/types/step_manager';
import { PrivacyPolicyCard } from '../privacy_policy_card/PrivacyPolicyCard';
import { CoinsIcon } from '../icon/coins_icon/CoinsIcon';
import { CreditRange } from '../../types/credit_range.enum';
import { isGarageFlow } from '../../utils/garage';

import { StepTrustHeader } from './components/step_trust_header/StepTrustHeader';
import { MobileButtonFloatFlagContext } from './utils/mobile_button_float_context';
import { StepNavType } from './components/step_nav/type';
import { getStepNav } from './components/step_nav/StepNavContext';
import { StepAnimationContainer } from './components/step_animation_container/StepAnimationContainer';
import Confetti from './imgs/confetti-bg.svg';
import { TrustList } from './components/TrustList';

const OverScrollBehaviorNone = createGlobalStyle`
  /** html works for safari and body works for android  */
  html, body {
    overscroll-behavior: none;
  }
`;

interface StepContainerProps {
  className?: string;
  route: string;
  direction: Direction;
  goNext?: () => void;
  goBack?: () => void;
  stepIdx?: number;
  stepCnt?: number;
  noProgressBar?: boolean;
  stepNavType?: StepNavType;
  noBottomContent?: boolean;
  showConfetti?: boolean;
  confettiGrey?: boolean;
  showLoader?: boolean;
  showTrustViewCard?: boolean;
  customHeader?: JSX.Element;
  isMobile?: boolean;
  /** Icon hidden on small screens */
  icon?: FunctionComponent<{ size?: number }> | JSX.Element;
  /** Gray text below the icon - hidden with icon on small screens */
  iconText?: ReactNode;
  title?: ReactNode;
  /** Text under the title */
  subtitle?: JSX.Element | string;
  /** The indicator data of the step.*/
  stepIndicatorData?: StepIndicatorDataType;
  /** Only show step category in step title*/
  categoryName?: string;
  /** Indicates not to show step trust header.*/
  noStepTrustHeader?: boolean;
  /** Display the message in phone page */
  showBoxAbovePhonePage?: boolean;
  /** Content to display before the trust list */
  beforeTrustList?: ReactNode;
  showTrustElement?: boolean;
  showPrivacyPolicyCard?: boolean;
  containerFloatingIcon?: JSX.Element;
  showIcon?: boolean;
}

/**
 * Container for a single step.
 * Also responsible for animating the navigation between steps.
 */
const UnstyledStepContainer: FunctionComponent<StepContainerProps> = ({
  className,
  route,
  direction,
  goNext,
  goBack,
  stepIdx,
  stepCnt,
  stepIndicatorData,
  children,
  noProgressBar = false,
  stepNavType,
  noBottomContent = true,
  showConfetti = false,
  showLoader = false,
  showTrustViewCard = true,
  customHeader,
  isMobile = false,
  icon: Icon,
  iconText,
  title,
  subtitle,
  categoryName,
  noStepTrustHeader = false,
  showBoxAbovePhonePage = false,
  beforeTrustList,
  showPrivacyPolicyCard = true,
  showTrustElement: showTrustElementProp = true,
  containerFloatingIcon,
  showIcon = false,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Set default value for 'noFloatingButton' context.
  const [noFloatingButton, setNoFloatingButton] = useState(false);

  // Hide icon and subtitle if screen is small.

  /**
   * Check window height vs 'StepContainer's height and set mobile floating flag.
   */
  useEffect(() => {
    const onResize = () => {
      // Don't float main CTA if user is not on mobile.
      if (!isMobile) {
        setNoFloatingButton(true);
        return;
      }

      if (containerRef.current) {
        // Check if 'StepContainer' can fit into window.
        const isContainerSmallerThanWindow = window.innerHeight > containerRef.current.getBoundingClientRect().height;

        // Set floating flag conext.
        setNoFloatingButton(isContainerSmallerThanWindow);
      }
    };

    onResize();

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [setNoFloatingButton, isMobile]);

  const { appData } = useStepManager(route);
  const stepTrustHeaderText = getStepTrustHeaderText(route, appData);
  /** show trust element in bottom */
  const showTrustElement = showTrustElementProp && !appData.id;
  /** Do not show trust review card in garage/dwep flow. */
  const noTrustReviewCard = appData.selectedService?.dwep;

  /**
   * Show steps by category in new flow(garage/dwep). Step title and progress bar are calculated based on it.
   */
  const calculateByCategory = (appData.selectedService?.dwep || appData.selectedService?.refi) && stepIndicatorData;

  let showTrustCard = showTrustViewCard && showTrustElement && !noTrustReviewCard;
  let showTrustAndDownloadCard = false;
  if (route === 'welcome' || route === 'prefilladditionalinfo' || route === 'prefillphone') {
    showTrustCard = showTrustViewCard && !noTrustReviewCard;
    showTrustAndDownloadCard = true;
  }

  const creditRangeText = useMemo(() => {
    switch (appData.signupParams?.creditRange) {
      case CreditRange.Between550and659:
        return ' and credit between 550-659';
      case CreditRange.Between660and750:
        return ' and credit between 660-750';
      case CreditRange.MoreThan750:
        return ' and credit more than 750';
      case CreditRange.LessThan550:
      default:
        return '';
    }
  }, [appData.signupParams?.creditRange]);

  const loadingContainerRef = useRef<HTMLDivElement | null>(null);
  const StepNavElem = useMemo(() => {
    if (!isNil(stepNavType)) {
      return getStepNav(stepNavType);
    }
    return getStepNav(appData.flowType ? StepNavType.GarageOnly : StepNavType.Default);
  }, [stepNavType, appData.flowType]);

  const progress = useMemo(() => {
    return undefined;
    // Keep this commented out code for now, as it is not clear if it is needed.
    // return stepNavContext.progress?.allProgress;
  }, []);

  return (
    <>
      {
        // Prevent pulling to refresh in case the user accidentally refresh the page.
        <OverScrollBehaviorNone />
      }
      <MobileButtonFloatFlagContext.Provider value={{ noFloatingButton, setNoFloatingButton }}>
        <div ref={containerRef}>
          <Box
            flex={noBottomContent ? 1 : 0}
            className={className}
            pb={noBottomContent ? (isGarageFlow(appData) ? 3 : 10) : 0}
            sx={containerFloatingIcon ? { position: 'relative' } : undefined}
          >
            {StepNavElem && (
              <StepNavElem
                goNext={goNext}
                goBack={goBack}
                stepIdx={stepIdx}
                stepCnt={stepCnt}
                categories={stepIndicatorData?.categories}
                stepIndexInCategory={stepIndicatorData?.stepIndexInCategory}
                stepCategoryCount={stepIndicatorData?.stepCategoryCount}
                stepCategoryName={stepIndicatorData?.categoryOfCurrentStep}
                appData={appData}
                categoryName={categoryName}
              />
            )}

            <StepAnimationContainer direction={direction}>
              <Box className="stepBox">
                {showBoxAbovePhonePage && (
                  <Box className="boxAboveStepBody">
                    <CoinsIcon></CoinsIcon>
                    <div
                      style={{
                        marginLeft: '0.5rem',
                        alignSelf: 'center',
                      }}
                    >
                      <div className="boxAboveStepBodyTitle">Drivers like you can get a quote under $125/mo </div>
                      <div className="boxAboveStepBodyBody">
                        Why? No claims{creditRangeText ? ',' : ' and'} no violations{creditRangeText}
                      </div>
                    </div>
                  </Box>
                )}
                <Paper elevation={2} className={`stepPaper shadowRounded ${showConfetti ? 'confetti' : ''}`}>
                  {!noStepTrustHeader && stepTrustHeaderText && !isEmpty(stepTrustHeaderText) && (
                    <StepTrustHeader className="step-trust-header" stepTrustHeaderText={stepTrustHeaderText} />
                  )}
                  {/* Display custom header if any */}
                  {customHeader}
                  {!noProgressBar && (
                    <>
                      <div className="progressBarContainer">
                        <div
                          className={calculateByCategory ? 'progressBarByCategory' : 'progressBar'}
                          style={
                            typeof progress === 'number'
                              ? {
                                  width: `${progress * 100}%`,
                                }
                              : undefined
                          }
                        />
                      </div>
                      {typeof progress === 'number' && (
                        <div className="progressTextContainer" style={{ width: `${progress * 100}%` }}>
                          <div className="progressText">{Math.round(progress * 100)}%</div>
                        </div>
                      )}
                    </>
                  )}
                  {/* Display a loader covering the step container */}
                  <AnimatePresence>
                    {showLoader && (
                      <motion.div
                        className="loadingContainer"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        ref={loadingContainerRef}
                      >
                        <Loader screenCenter containerRef={loadingContainerRef} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <Box className="stepBodyContainer">
                    {containerFloatingIcon}

                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      data-cy="pageMainIcon"
                    >
                      {showIcon && Icon && !isGarageFlow(appData) && (
                        <Box
                          className="hideOnSmallScreen"
                          // If not flex, the container will be slightly higher than the SVG child.
                          // See https://stackoverflow.com/questions/11126685/why-does-container-div-insist-on-being-slightly-larger-than-img-or-svg-content
                          display="flex"
                          mb={3}
                        >
                          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                          {/* @ts-ignore */}
                          {React.isValidElement(Icon) ? Icon : <Icon />}
                        </Box>
                      )}
                      {iconText && (
                        <Box mb={1}>
                          <Typography color="textSecondary" align="center" data-cy="pageMainSubTitle">
                            {iconText}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {title && (
                      <Box mb={subtitle ? 1 : 4}>
                        <Typography className="step-title" variant="h4" align="center" data-cy="pageMainTitle">
                          {title}
                        </Typography>
                      </Box>
                    )}
                    {subtitle && (
                      <Box mb={4} data-cy="pageMainSubTitle">
                        <Typography className="step-subtitle" color="textSecondary" align="center">
                          {subtitle}
                        </Typography>
                      </Box>
                    )}
                    {children}
                  </Box>
                </Paper>

                {beforeTrustList}

                <Box mt={2.5}>
                  <TrustList
                    items={compact([
                      showTrustCard && showPrivacyPolicyCard && (
                        <PrivacyPolicyCard selectedService={appData.selectedService} flowType={appData.flowType} />
                      ),
                      showTrustElement && (
                        <TrustText
                          mobileOS={appData.mobileOS}
                          selectedService={appData.selectedService}
                          appData={appData}
                        />
                      ),
                      showTrustAndDownloadCard && <TrustAndDownloadCard />,

                      showTrustCard && (
                        <TrustReviewCard
                          route={route}
                          selectedService={appData.selectedService}
                          flowType={appData.flowType}
                          hideStars={showTrustAndDownloadCard} // Because TrustAndDownloadCard also has star
                        />
                      ),
                      !showTrustCard && showPrivacyPolicyCard && (
                        <PrivacyPolicyCard selectedService={appData.selectedService} flowType={appData.flowType} />
                      ),
                    ])}
                  />
                </Box>
              </Box>
            </StepAnimationContainer>
          </Box>
        </div>
      </MobileButtonFloatFlagContext.Provider>
    </>
  );
};

export const StepContainer = styled(UnstyledStepContainer)`
  .shadowRounded {
    border-radius: 0.5rem;
    box-shadow: 0px 12px 44px 0px rgba(0, 0, 0, 0.1);
  }

  .stepBox {
    padding: 0px 16px;
    @media (max-width: ${({ theme }) => theme.breakpoints.ipad}px) {
      padding: 0px;
    }
  }

  .stepPaper {
    position: relative;
    overflow: hidden;
  }

  .confetti {
    background-repeat: repeat-x;
    background-image: ${({ theme, confettiGrey }) =>
      `url('${themeSVGBase64(
        Confetti,
        '#FF3975',
        confettiGrey ? theme.palette.grey[200] : theme.palette.primary.main
      )}')`};
  }

  .progressBarContainer {
    top: 0px;
    left: 0px;
    width: 100%;
    height: 7px;
    border-radius: ${(stepTrustHeaderText) => (stepTrustHeaderText ? '0px 0px 0px 0px;' : '8px 8px 0px 0px')};
    overflow: hidden;
  }
  .progressBar {
    background-color: ${({ theme }) => theme.palette.primary.main};
    width: ${({ stepIdx, stepCnt }) => (stepCnt ? (((stepIdx || 0) + 1) / stepCnt) * 100 : 0)}%;
    height: 100%;
    transition: width 1s ease 0s;
  }
  .progressBarByCategory {
    background-color: ${({ theme }) => theme.palette.primary.main};
    width: ${({ stepIndicatorData }) =>
      stepIndicatorData?.stepIndexInWholeSteps
        ? (((stepIndicatorData?.stepIndexInWholeSteps || 0) + 1) / stepIndicatorData?.stepCountInWholeSteps) * 100
        : 0}%;
    height: 100%;
    transition: width 1s ease 0s;
  }
  .progressTextContainer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    transition: width 1s ease 0s;
    .progressText {
      background-color: ${({ theme }) => theme.palette.primary.main};
      color: #fff;
      font-size: 12px;
      padding: 0 6px;
      font-weight: 500;
      border-radius: 0 0 6px 6px;
      position: relative;
      top: -6px;
    }
  }

  .stepBodyContainer {
    margin: 0 auto;
    padding: 2.5rem 1.25rem 2rem 1.25rem;
    max-width: 450px;
  }

  .loadingContainer {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    z-index: 2;
  }

  // All cards can be fully shown on a 810px height viewport.
  @media (max-height: 810px) {
    .hideOnSmallScreen {
      display: none;
    }
  }

  .boxAboveStepBody {
    border: 1px solid #d9e0e9;
    border-radius: 8px;
    margin-bottom: 0.5rem;
    padding: 1rem 1rem;
    display: flex;
    flex-direction: row;
    align-content: center;
  }
  .boxAboveStepBodyTitle {
    font-family: ${({ theme }) => theme.font.circular};
    font-weight: 500;
    font-size: 13px;
    line-height: 18px;
  }
  .boxAboveStepBodyBody {
    color: #6b7a86;
    font-size: 12px;
    line-height: 15px;
  }

  & .step-title {
    font-size: 28px;
    font-weight: 900;
    line-height: 31px;
    letter-spacing: -1.5px;
  }
`;
